import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  HeadingThree,
  DynamicSelect,
  Form,
  Button,
  minWidth,
  lessThanWidth,
  colors,
  HeadingFour,
  SmallText,
  validation
} from 'hui';
import { supo } from '../../api';
import HeroBackgroundImage from './Images/hero_background.jpg';
import { getThumborURL } from '../../utils/thumbor';

//styles
const Background = styled.div`
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.background});
  background-size: cover;
  opacity: 0.5;
  background-position-x: 50%;
  background-position-y: 20%;
`;

const HeroWrapper = styled.div`
  background-color: #000;
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${minWidth('lg')} {
    min-height: 30em;
  }
  ${lessThanWidth('lg')} {
    min-height: 20em;
    margin-bottom: 5em;
  }
`;

const FormFrame = styled.div`
  ${minWidth('lg')} {
    width: 60%
  }
  ${lessThanWidth('lg')} {
    width: 80%
  }
  background: ${colors.tint0}
  &::after {
    display: block;
    clear: both;
  }
  z-index: 1;
`;

const SelectFrame = styled.div`
  ${props =>
    props.error &&
    css`
      & .select__placeholder {
        color: red;
      }
      & .select__control {
        border: red 2px solid !important;
      }
    `}
  ${minWidth('lg')} {
    width: 70%;
    float: left;
  }
`;

const StyledButton = styled(Button)`
  ${minWidth('lg')} {
    width: 30%;
    float: right;
  }
  ${lessThanWidth('lg')} {
    width: 100%;
  }
`;

const StyledHeading = styled(HeadingThree)`
  padding-top: 1em;
`;

const ContentFrame = styled.div`
  padding-top: 2em;
  padding-bottom: 2em;
`;

const TextStyle = styled.span`
  color: ${colors.tint6};
`;

const BottomFrame = styled.div`
  max-width: 580px;
  margin: 4rem auto 0;
`;

interface HeroProps {
  title: string;
  setModalData: any;
  initialValue: any;
  fallbackQuestions: any;
  fallbackCategoryDetails: any;
  heroImage: string;
}

interface FormValue {
  category: string;
}

const getMappedData = data => {
  return data.map(item => {
    const { name, key } = item;
    return { label: name, value: key };
  });
};

const api = 'https://hipages.com.au/api/getquotes/categories?query=';

const getModalDataFromKey = async (key: string) => {
  try {
    const response = await supo.get(`/category-questions/${key.toLowerCase()}`);
    return response.data;
  } catch (e) {
    return { categoryDetails: {}, questions: {} };
  }
};

const Hero: React.FC<HeroProps> = props => {
  const {
    title,
    setModalData,
    initialValue,
    fallbackQuestions,
    fallbackCategoryDetails,
    heroImage
  } = props;

  const [validationError, setValidationError] = useState(false);

  const dynamicSelectData = {
    name: 'category',
    api,
    mapFn: getMappedData,
    changeCallback: values => openModalWithData(values.value),
    placeholder: `Start typing a trade, e.g. Plumber`,
    noOptionMessage: '',
    isOpenWithDefaultValue: true,
    isAutoCorrect: true
  };

  const validate = values => {
    const trimmedValues = { ...values, category: values.category.trim() };
    const errors = validation.required(trimmedValues, {
      Category: 'category'
    });
    setValidationError(!!errors.category);
  };

  const onSubmit = async (value: FormValue) => {
    if (!validationError) {
      await openModalWithData(value.category ? value.category : 'invalid_category');
    }
  };

  const openModalWithData = async (category: string) => {
    const { categoryDetails, questions } = await getModalDataFromKey(category);
    const updatedFallbackCategoryDetails = {
      ...fallbackCategoryDetails,
      key: category ? category : fallbackCategoryDetails.key
    };
    setModalData({
      categoryDetails:
        categoryDetails && categoryDetails.key ? categoryDetails : updatedFallbackCategoryDetails,
      questions: questions && questions.tree ? questions : fallbackQuestions,
      isOpen: true
    });
  };

  const formData = {
    onSubmit,
    validate,
    initialValues: { category: initialValue }
  };

  const heroBackGroundImage = heroImage ? heroImage : HeroBackgroundImage;

  return (
    <HeroWrapper>
      <Background background={getThumborURL(heroBackGroundImage)} />
      <StyledDiv>
        <FormFrame>
          <StyledHeading align="center" color="tint6" as="h1">
            {title}
          </StyledHeading>
          <ContentFrame>
            <Form {...formData}>
              <SelectFrame error={validationError}>
                <DynamicSelect {...dynamicSelectData} />
              </SelectFrame>
              <StyledButton>Get Quotes</StyledButton>
            </Form>
          </ContentFrame>
          <BottomFrame>
            <TextStyle>
              <HeadingFour as="h2" align="center">
                What customers say about hipages
              </HeadingFour>
              <SmallText align="center">
                {`"Excellent service - use them frequently and always get great results with the
                    tradies recommended. I don't bother looking elsewhere anymore I just use hipages
                    as it's so easy and they are fantastic to deal with." - Catherine O`}
              </SmallText>
            </TextStyle>
          </BottomFrame>
        </FormFrame>
      </StyledDiv>
    </HeroWrapper>
  );
};

export default Hero;
